import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'
import {
  AuthenticationProvider,
  Login,
  Invitation
} from '@mysaiva/core/authentication'
import { ZendeskRedirect, ZendeskWebWidget } from '@mysaiva/core/zendesk'
import { createApiClient, applyResponseInterceptors } from '@mysaiva/core/api'
import { ThemeProvider, ToastProvider, createAntdToast } from '@mysaiva/core'
import { LaunchDarklyProvider, getAnonymousUserKey } from '@mysaiva/core/launchdarkly'
import { DEVELOPER_PLATFORM_PATH } from '@mysaiva/core/developer-platform'
import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import { PostLoginRedirect } from './components/PostLoginRedirect'
import { ProductAccessCheck } from './components/ProductAccessCheck'
import { LegacyUptRedirect } from './components/LegacyUptRedirect'
import { SaivaProduct } from './utils/productRedirect'
import { i18next } from './i18n'
import { ErrorPage } from './components/ErrorPage'
import { DeveloperPlatformRoutes } from './features/developer-platform'

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
})

// Create toast notification adapter for ToastProvider
const toastNotification = createAntdToast()

function App() {
  // Create API client with response interceptors for proper error handling
  const apiClient = useMemo(() => {
    const client = createApiClient(import.meta.env.VITE_REACT_APP_SAIVA_BACKEND_URL)
    applyResponseInterceptors(client)
    return client
  }, [])

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <ThemeProvider>
        <I18nextProvider i18n={i18next}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider toast={toastNotification}>
              {/* Feature flags. Anonymous identity until the Developer Platform
                  providers identify the signed-in user + organisation. Without
                  VITE_LAUNCHDARKLY_CLIENT_ID the provider runs disabled and every
                  flag returns its default. */}
              <LaunchDarklyProvider
                clientSideId={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID}
                user={{
                  user_id: getAnonymousUserKey(),
                  anonymous: true,
                  product: 'shell',
                  environment: import.meta.env.MODE
                }}
                onInitError={err => Sentry.captureException(err)}
              >
                <BrowserRouter basename="/">
                  <AuthenticationProvider apiClient={apiClient}>
                    <Routes>
                    {/* Public auth routes */}
                    <Route path="/login" element={<Login apiClient={apiClient} />} />
                    <Route path="/login/:email" element={<Login apiClient={apiClient} />} />
                    <Route path="/join/:token" element={<Invitation apiClient={apiClient} />} />

                    {/* Error page */}
                    <Route path="/error" element={<ErrorPage />} />

                    {/* Zendesk SSO redirect */}
                    <Route path="/zendesk/redirect" element={<ZendeskRedirect />} />

                    {/* Root route - handle post-login redirect to products */}
                    <Route path="/" element={<PostLoginRedirect apiClient={apiClient} />} />

                    {/* Legacy routes redirect - redirect paths without /upt prefix to /upt/* */}
                    <Route path="/resident/:residentToken" element={<LegacyUptRedirect />} />

                    {/* Developer Platform workspace (SAIV-10924). Hosted by the shell
                        for now; gated by feature flag + permissions inside. */}
                    <Route
                      path={`${DEVELOPER_PLATFORM_PATH}/*`}
                      element={<DeveloperPlatformRoutes apiClient={apiClient} />}
                    />

                    {/* Protected product routes - show error if user doesn't have access */}
                    <Route
                      path="/upt/*"
                      element={<ProductAccessCheck product={SaivaProduct.Upt} />}
                    />
                    <Route
                      path="/therapy/*"
                      element={<ProductAccessCheck product={SaivaProduct.Therapy} />}
                    />
                    <Route
                      path="/wounds/*"
                      element={<ProductAccessCheck product={SaivaProduct.WoundCare} />}
                    />
                    <Route
                      path="/compass/*"
                      element={<ProductAccessCheck product={SaivaProduct.Compass} />}
                    />

                    {/* Catch all - show error page for invalid routes */}
                    <Route path="*" element={<ErrorPage />} />
                    </Routes>
                    <ZendeskWebWidget />
                  </AuthenticationProvider>
                </BrowserRouter>
              </LaunchDarklyProvider>
            </ToastProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
